import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useCheckpointArtifactsState } from 'screens/Admin/ProjectCheckpoints/hooks/useCheckpointArtifactsState'
import { useCheckpointCriteriaState } from 'screens/Admin/ProjectCheckpoints/hooks/useCheckpointCriteriaState'
import AppealModal from 'screens/ProjectCheckpoints/components/AppealModal/AppealModal'
import ProjectCheckpointInfoCard from 'screens/ProjectCheckpoints/components/ProjectCheckpointInfoCard'
import SendWorkModal from 'screens/ProjectCheckpoints/components/SendWorkModal'
import useActiveProjectTeam from 'screens/ProjectCheckpoints/hooks/useActiveProjectTeam'
import { useProjectCheckpointInstances } from 'screens/ProjectCheckpoints/hooks/useProjectCheckpointInstances'
import useSelfActiveProject from 'screens/Projects/hooks/useSelfActiveProject'
import projectsApi from 'screens/Projects/projects.api'
import { ArtifactInspectionData } from 'screens/Projects/projects.types'
import eventBus from 'services/eventBus'
import Button from 'shared/Buttons/Button/Button'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import TopNavigationLayoutPageContent from 'shared/Layouts/TopNavigationLayout/TopNavigationLayoutPageContent'
import ThreeDotsLoader from 'shared/Loaders/ThreeDotsLoader'

const ProjectCheckpointPage = () => {
  const params = useParams()
  const id = params.id
  const selfProject = useSelfActiveProject()
  const activeTeam = useActiveProjectTeam()
  const teamId = activeTeam?.id
  const { instances, loading: instancesLoading } = useProjectCheckpointInstances(selfProject?.id, teamId)

  const checkpointInstance = useMemo(() => {
    return instances.find((instance) => instance.id === id)
  }, [instances, id])

  const { criteriaState, loading: criteriaLoading } = useCheckpointCriteriaState(checkpointInstance?.id)
  const { artifactsState, loading: artifactsLoading } = useCheckpointArtifactsState(checkpointInstance?.id)

  const { wait } = useHttpLoader()

  const [isSendWorkModalOpen, setIsSendWorkModalOpen] = useState<boolean>(false)
  const [isAppealModalOpen, setIsAppealModalOpen] = useState<boolean>(false)

  const canSendWork = checkpointInstance?.status === 'OPEN' || checkpointInstance?.status === 'OVERDUE'
  const canAppeal = checkpointInstance?.status === 'INSPECTED'

  const openSendWorkModal = () => {
    setIsSendWorkModalOpen(true)
  }

  const openAppealModal = () => {
    eventBus.pub('ntf.message', { type: 'info', message: 'Функционал временно не работает' })
    // setIsAppealModalOpen(!isAppealModalOpen)
  }

  const handleSendWork = (artefacts: ArtifactInspectionData['artefacts']) => {
    if (!selfProject || !checkpointInstance) return

    const data: ArtifactInspectionData = {
      artefacts,
      projectId: selfProject?.id,
      checkpointId: checkpointInstance?.id!,
    }
    wait(projectsApi.submitProjectCheckpointArtefactsToInspect(selfProject.id, checkpointInstance.id, data), (resp) => {
      if (resp.status === 'success') {
        setIsSendWorkModalOpen(!isSendWorkModalOpen)
        eventBus.pub('ntf.message', { type: 'info', message: 'Работа успешно отправлена на проверку' })
      }
    })
  }

  if (!checkpointInstance || instancesLoading || criteriaLoading || artifactsLoading) {
    return (
      <div className='flex-page-center'>
        <ThreeDotsLoader />
      </div>
    )
  }

  return (
    <TopNavigationLayoutPageContent type='defaultWithBackButton'>
      <div className='flex-lines'>
        <div className='flex-space-between' style={{ width: '100%' }}>
          <h2 className='text--heading-h4'>{checkpointInstance?.name}</h2>
          {canSendWork && (
            <Button fullWidth={false} onClick={openSendWorkModal}>
              Сдать работу
            </Button>
          )}
          {canAppeal && (
            <Button fullWidth={false} onClick={openAppealModal}>
              Апелляция
            </Button>
          )}
        </div>
        <div>
          <ProjectCheckpointInfoCard
            checkpoint={checkpointInstance}
            stage={checkpointInstance.stage}
            checkpointCriteria={criteriaState}
          />
        </div>
      </div>
      {isSendWorkModalOpen && (
        <SendWorkModal
          onClose={() => setIsSendWorkModalOpen(false)}
          artifacts={artifactsState}
          onSave={handleSendWork}
        />
      )}
      {isAppealModalOpen && (
        <AppealModal onClose={() => setIsAppealModalOpen(!isAppealModalOpen)} artifacts={artifactsState} />
      )}
    </TopNavigationLayoutPageContent>
  )
}

export default ProjectCheckpointPage
